import React, { useContext, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/SendOutlined';
import Button from 'components/Button';
import IconButton from 'components/IconButton';
import TextInput from 'components/form/TextInput';
import { ConfigContext } from 'context/config';
import { DCC } from 'constants/index';
import { TEMPLATES } from 'services/rabbit.service';
import { APP_CONFIG, REACT_APP_UI_URL } from 'constants/config';
import { sendEmailInstruction } from 'services/groups.service';
import toast, { snack } from 'utils/toast';
import WixIcon from '../../assets/wix.png';
import WordpressIcon from '../../assets/wordpress.png';
import SquarespaceIcon from '../../assets/squarespace.png';

const SCRIPT = `<script type='text/javascript'>!function(t,e,c,n,d,s,i){t.TicWidget='td',t.td=t.td||function(){(t.td.queue=t.td.queue||[]).push(arguments)},t.td.l=1*new Date,s=e.createElement('script'),i=e.getElementsByTagName('script')[0],s.async=1,s.src='${REACT_APP_UI_URL}/tic-dcc.js',i.parentNode.insertBefore(s,i)}(window,document);td('init', '__HASH__');</script>`;

const ScriptInstructions = ({ hash, onNext }) => {
  const [email, setEmail] = useState('');
  const account = useSelector((state) => state.settings.account);
  const code = SCRIPT;
  const script = code.replace('__HASH__', hash);
  const [t] = useTranslation('connectCard');
  const { BRAND } = useContext(ConfigContext);

  const sendInstructions = async () => {
    const accountName = account.account_name;

    const request = {
      hash: TEMPLATES.SCRIPT_INSTRUCTIONS[APP_CONFIG.STAGE][
        account.account_brand
      ],
      type: 'send_email_template',
      account_id: account.account_id,
      account_name: accountName,
      scc_hash: hash,
      scc_url: DCC.URL,
      to_email: email,
    };

    return sendEmailInstruction(request).then(() => {
      toast.success(t('cc-email-sent', 'Instruction sent!'));
      setEmail('');
    });
  };

  return (
    <Paper className="script-instructions" sx={{ p: 3 }}>
      <Box mb={4}>
        <Typography paragraph fontWeight={500}>
          {t('cc-form-script-title', 'Link To Your Website')}
        </Typography>
        <Typography paragraph color="textSecondary">
          {t(
            'cc-form-script-desc',
            'Copy & paste the following code before the closing tag on every page of your site that you would like to display your Connect Widget.',
          )}
        </Typography>
      </Box>
      <Box bgcolor="background.default" p={2} mb={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography fontWeight={500}>Here’s Your Code Snippet</Typography>
          <CopyToClipboard
            text={script}
            onCopy={() => snack.success('Code copied!')}
          >
            <Button
              size="small"
              color="primary"
              startIcon={<ContentCopyIcon />}
              variant="text"
            >
              Copy to clipboard
            </Button>
          </CopyToClipboard>
        </Stack>
        <Paper sx={{ p: 2 }}>
          <code>{script}</code>
        </Paper>
      </Box>
      <Typography paragraph>3rd Party Platforms</Typography>
      <Typography sx={{ mb: 2 }} color="textSecondary">
        <Trans i18nKey="cc-form-script-instructions">
          If you use a third-party platform (Wordpress, Squarespace, Wix) you
          can find the instructions by clicking on the following link buttons.
        </Trans>
      </Typography>
      <Stack
        direction="row"
        justifyContent="center"
        spacing={2}
        mb={4}
        sx={{
          '.MuiButton-outlined': {
            borderRadius: '8px',
            borderColor: 'divider',
            boxShadow: '0px 5px 4px -1px rgba(0, 0, 0, 0.09)',
          },
        }}
      >
        <a
          target="_blank"
          href="https://help.textinchurch.com/en/articles/1922335-adding-the-smart-connect-card-script-to-your-website#wix"
        >
          <Button variant="outlined">
            <img src={WixIcon} style={{ height: 14 }} />
          </Button>
        </a>
        <a
          target="_blank"
          href="https://help.textinchurch.com/en/articles/1922335-adding-the-smart-connect-card-script-to-your-website#wordpress"
        >
          <Button variant="outlined">
            <img src={WordpressIcon} style={{ height: 14 }} />
          </Button>
        </a>
        <a
          target="_blank"
          href="https://help.textinchurch.com/en/articles/1922335-adding-the-smart-connect-card-script-to-your-website#squarespace"
        >
          <Button variant="outlined">
            <img src={SquarespaceIcon} style={{ height: 14 }} />
          </Button>
        </a>
      </Stack>
      <Box mb={3}>
        <TextInput
          fullWidth
          margin="none"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          label={t(
            'cc-from-script-send-title',
            'Email instructions to your Developer',
          )}
          placeholder={t('cc-form-script-email-placeholder', 'Email Address')}
          endAdornment={
            <IconButton
              disabled={!email}
              edge="end"
              color="primary"
              variant="text"
              onClick={sendInstructions}
              icon={<SendIcon />}
            />
          }
        />
      </Box>
      <Box ml="auto" width={100} mt={2}>
        <Button fullWidth onClick={onNext}>
          Next
        </Button>
      </Box>
    </Paper>
  );
};

export default ScriptInstructions;
